// src/app/contexts/JWTAuthContext.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { createContext, useEffect, useReducer, useRef, useCallback } from "react";
// GLOBAL CUSTOM COMPONENTS
import Loading from "app/components/ExpLoading";
// Logger
import logger from '../utils/logger';

const LOG_LOC = { location: `${process.env.REACT_APP_LOG_PREFIX} 'src/app/contexts/JWTAuthContext.js'` }

// Set the base URL for axios
const API_BASE_URL = process.env.REACT_APP_RUN_ENV === 'development'
  ? process.env.REACT_APP_API_BASE_URL_DEV || ''
  : process.env.REACT_APP_API_BASE_URL || '';
axios.defaults.baseURL = API_BASE_URL;

const initialState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false
};

const isValidToken = (accessToken) => {
  if (!accessToken) return false;
  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", JSON.stringify({ token: accessToken }));
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem("accessToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return { ...state, isAuthenticated, isInitialized: true, user };
    }
    case "LOGIN": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: true, user };
    }
    case "LOGOUT": {
      return { ...state, isAuthenticated: false, user: null };
    }
    case "REGISTER": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: true, user };
    }
    default: {
      return state;
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => { },
  register: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const refreshTimerRef = useRef(null);
  const isRefreshingTokenRef = useRef(false); // Use useRef to persist across renders

  // logging
  // useEffect(() => {
  //   logger.info('JWTAuthContext component mounted', LOG_LOC);

  //   return () => {
  //     logger.info('JWTAuthContext component unmounted', LOG_LOC);
  //   };
  // }, []);

  const stopTokenRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      logger.info("Stopping token refresh timer", LOG_LOC);
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    dispatch({ type: "LOGOUT" });
    stopTokenRefreshTimer();
  }, [stopTokenRefreshTimer]);

  const scheduleNextTokenRefresh = (token) => {
    const decodedToken = jwtDecode(token);
    const expiresAt = decodedToken.exp * 1000;
    const currentTime = Date.now();
    const refreshIn = expiresAt - currentTime - process.env.REACT_APP_TOKEN_REFRESH_BFR; // Refresh milli seconds before expiration
    //const refreshIn = expiresAt - currentTime - 10000; // Refresh 5 seconds before expiration

    if (refreshIn > 0) {
      refreshTimerRef.current = setTimeout(refreshToken, refreshIn);
      logger.info(`Scheduled next token refresh in ${refreshIn} ms`, LOG_LOC);
    } else {
      // Token is about to expire or already expired, refresh immediately
      refreshToken();
    }
  };

  const refreshToken = useCallback(async () => {
    if (isRefreshingTokenRef.current) {
      // Prevent overlapping calls
      return;
    }
    isRefreshingTokenRef.current = true;

    try {
      logger.info("Attempting to refresh token...", LOG_LOC);
      const storedToken = localStorage.getItem("accessToken");
      const token = storedToken ? JSON.parse(storedToken).token : null;

      if (!token) {
        logger.info("No token found in localStorage", LOG_LOC);
        throw new Error("No token available");
      }

      //logger.debug('Token being sent for refresh: ' + token, LOG_LOC);

      const response = await axios.get("/api/auth/refresh", {
        headers: { Authorization: `Bearer ${token}` }
      });

      //logger.debug('Refresh response: ' + response.data.token, LOG_LOC);

      if (response.data.ok && response.data.token) {
        const newToken = response.data.token;
        setSession(newToken);
        const user = jwtDecode(newToken);
        dispatch({ type: "LOGIN", payload: { user } });
        scheduleNextTokenRefresh(newToken); // Schedule next refresh based on new token
        //logger.debug('scheduleNextTokenRefresh newToken: ' + newToken, LOG_LOC);
        return newToken;
      } else {
        throw new Error("Invalid response from refresh endpoint");
      }
    } catch (error) {
      logger.error('Error refreshing token: ' + error, LOG_LOC);
      if (error.response) {
        logger.error('Response status: ' + error.response.status, LOG_LOC);
        logger.error('Response data: ' + error.response.data, LOG_LOC);
      }
      logger.info('Before logout by force', LOG_LOC);
      logout();
      return null;
    } finally {
      isRefreshingTokenRef.current = false;
    }
  }, [logout]);

  const startTokenRefreshTimer = useCallback((token) => {
    stopTokenRefreshTimer();
    scheduleNextTokenRefresh(token);
  }, [stopTokenRefreshTimer]);

  const login = async (userid, password) => {
    try {
      logger.info('=Login start=', LOG_LOC);
      const response = await axios.post("/api/auth/login", { userid, password });
      const { ok, token, errorMessage } = response.data;

      if (ok && token) {
        setSession(token);
        const user = jwtDecode(token);
        dispatch({ type: "LOGIN", payload: { user } });
        startTokenRefreshTimer(token);
        return { ok: true };
      } else {
        logger.error('로그인 실패: ' + errorMessage, LOG_LOC);
        return { ok: false, errorMessage };
      }
    } catch (error) {
      logger.error('로그인 오류: ' + error, LOG_LOC);
      return { ok: false, errorMessage: "예기치 않은 오류 발생" };
    }
  };

  const register = async (userid, password, adminLevel) => {
    try {
      logger.info('사용자 등록 시작', LOG_LOC);
      const response = await axios.post("/api/auth/signup", { userid, password, adminLevel });
      const { ok, accessToken: token, user, errorMessage } = response.data;

      if (ok && token && user) {
        setSession(token);
        dispatch({ type: "REGISTER", payload: { user } });
        startTokenRefreshTimer(token);
        logger.info('사용자 등록 성공', LOG_LOC);
        return { ok: true };
      } else {
        logger.error(`사용자 등록 실패: ${errorMessage}`, LOG_LOC);
        return { ok: false, errorMessage: errorMessage || "등록 중 오류가 발생했습니다.\n다시 시도해 주시기 바랍니다." };
      }
    } catch (error) {
      logger.error(`사용자 등록 오류: ${error.message}`, LOG_LOC);
      if (error.response) {
        logger.error(`Response status: ${error.response.status}`, LOG_LOC);
        logger.error(`Response data: ${JSON.stringify(error.response.data)}`, LOG_LOC);
        return {
          ok: false,
          errorMessage: error.response.data.message || "등록 중 오류가 발생했습니다.\n다시 시도해 주시기 바랍니다."
        };
      }
      return {
        ok: false,
        errorMessage: "등록 중 오류가 발생했습니다.\n다시 시도해 주시기 바랍니다."
      };
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        if (accessToken) {
          const { token } = JSON.parse(accessToken);
          if (isValidToken(token)) {
            setSession(token);
            const user = jwtDecode(token);
            dispatch({
              type: "INIT",
              payload: { isAuthenticated: true, user }
            });
            startTokenRefreshTimer(token);
          } else {
            logger.info("Stored token is invalid, attempting refresh", LOG_LOC);
            const newToken = await refreshToken();
            if (newToken) {
              startTokenRefreshTimer(newToken);
            } else {
              dispatch({
                type: "INIT",
                payload: { isAuthenticated: false, user: null }
              });
            }
          }
        } else {
          logger.warn("No token(accessToken) found in storage", LOG_LOC);
          dispatch({
            type: "INIT",
            payload: { isAuthenticated: false, user: null }
          });
        }
      } catch (err) {
        logger.error('Auth initialization error: ' + err, LOG_LOC);
        dispatch({
          type: "INIT",
          payload: { isAuthenticated: false, user: null }
        });
      }
    })();

    return () => {
      stopTokenRefreshTimer();
    };
  }, [refreshToken, startTokenRefreshTimer, stopTokenRefreshTimer]);

  if (!state.isInitialized) return <Loading />;

  return (
    <AuthContext.Provider value={{ ...state, method: "JWT", login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;